import { Component, ElementRef, HostListener, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { IllustrationService } from 'src/app/services/illustration/illustration.service';
import { DOCUMENT, Location } from "@angular/common";
import { AccountDto, CountryDto, CountyDto, CustomFile, IllustrationRequestDto, Status, TasksStatus, UserDto } from 'src/app/web-api-client';
import { fromEvent, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map } from 'rxjs/operators';
import { AccountsService } from 'src/app/services/accounts/accounts.service';
import { UserService } from 'src/app/services/user.service';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { NetworkMembersService } from 'src/app/services/network-members/network-members.service';
import { Title } from '@angular/platform-browser';
import { MessageType, PopupNotificationsService } from 'src/app/services/popup-notifications/popup-notifications.service';
import { faCircleXmark } from '@fortawesome/free-regular-svg-icons';
import { UserRoles } from 'src/app/models/enums/user-roles';
import { CountriesService } from 'src/app/services/countries/countries.service';
import { CommentService } from 'src/app/services/comment.service';
import { requestIllustration } from 'src/app/models/RequestIllustration';
import { Guid } from 'guid-typescript';
import { InformativeService } from 'src/app/services/informative.service';
import { IllustrationRequestInputMetadata } from 'src/app/models/illustrationRequestInputMetadata';

@Component({
  selector: 'app-request-illustration',
  templateUrl: './request-illustration.component.html',
  styleUrls: ['./request-illustration.component.scss']
})
export class RequestIllustrationComponent implements OnInit, OnDestroy {
  filteredClients: AccountDto[] = [];
  filteredCountries: CountryDto[] = [];
  filteredCounties: CountyDto[] = [];
  showClients = false;
  selectedClient: AccountDto;
  uploadedFiles: CustomFile[] = [];
  selectedCountry = new CountryDto();
  selectedCounty = new CountyDto();
  checkIsProspect = false;
  searchingClient = false;
  searchingCountry = false;
  searchingCounty = false;
  newClient = false;
  sendingRequest = false;
  isUSA = false;
  showWarningMessage = false;
  faCircleXmark = faCircleXmark;
  clickedOnClient = false;
  isCollaborator = false;
  displaySubs: UserDto[] = [];
  illustrations: IllustrationRequestDto[] = [];
  currentUserId: string;
  dragAreaClass: string;
  draggedFiles: any;
  requestIllustration = new requestIllustration();
  @ViewChild('searchClientField', { static: true }) searchInputClientField: ElementRef;
  @ViewChild('searchCountryField', { static: true }) searchInputCountryField: ElementRef;
  @ViewChild('searchCountyField', { static: true }) searchInputCountyField: ElementRef;
  subscriptions: Subscription[] = [];
  isCountrySelected = false;
  illustrationForm = new UntypedFormGroup({
    ClientName: new UntypedFormControl({ value: '', disabled: true }, [Validators.required]),
    Country: new UntypedFormControl('', [Validators.required]),
    State: new UntypedFormControl(''),
    AccountRepresentative: new UntypedFormControl(''),
    RegionalDirector: new UntypedFormControl(''),
    OpportunityId: new UntypedFormControl(''),
    Recipient: new UntypedFormControl('')
  });
  params: ParamMap;
  loadingIllustrations: boolean;
  networkMember: AccountDto;
  page: number;
  isNetworkMember: boolean;
  pageSize: number;
  hideShowMoreBtn: boolean;
  networkMemberId;
  showDiscussion = false;
  isCountySelected = false;
  constructor(
    private accountsService: AccountsService,
    private illustrationService: IllustrationService,
    public location: Location,
    private router: Router,
    private userService: UserService,
    @Inject(DOCUMENT) private document: Document,
    private networkMemberService: NetworkMembersService,
    private titleService: Title,
    private popupNotificationsService: PopupNotificationsService,
    private activatedroute: ActivatedRoute,
    private countryService: CountriesService,
    private commentService: CommentService,
    private informativeService: InformativeService
  ) {
    this.requestIllustration.id = Guid.create().toString();
    this.requestIllustration.processId = "illustration-input-request";
  }

  async ngOnInit(): Promise<void> {
    await this.countryService.loadCountries()
    this.commentService.task = this.requestIllustration;
    this.isCollaborator = await this.userService.isUserInRole(UserRoles.Collaborator);
    this.isNetworkMember = await this.userService.isUserInRole(UserRoles.NetworkMember);
    this.titleService.setTitle('Illustration Request | Insurope');
    this.activatedroute.queryParamMap.subscribe(async (params) => this.loadIllustrations(params));
    this.networkMemberId = (await this.userService.getCurrentUser()).networkMemberIds;
    const currentUser = await this.userService.getCurrentUser();
    this.currentUserId = currentUser.id;
    this.displaySubs.push(currentUser);
    this.networkMember = await this.networkMemberService.getNetworkMemberById(this.networkMemberId[0]);
    if (this.isCollaborator) {
      this.illustrationForm.controls.OpportunityId.setValidators([Validators.required]);
    }
    this.illustrationForm.controls.Country.setValue(null);
    this.setupSearchListeners();
  }

  setupSearchListeners() {
    this.subscriptions.push(fromEvent(this.searchInputClientField.nativeElement, 'keyup').pipe(
      map((event: any) => event.target.value),
      filter(res => res.length > 2),
      debounceTime(100),
      distinctUntilChanged()
    ).subscribe(text => this.fetchClient(text)));

    this.subscriptions.push(fromEvent(this.searchInputCountryField.nativeElement, 'keyup').pipe(
      map((event: any) => event.target.value),
      debounceTime(100),
      distinctUntilChanged()
    ).subscribe(text => this.fetchCountry(text)));
  }

  ngOnDestroy() {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  async loadClientByName(accountName) {
    this.clickedOnClient = true;
    this.router.navigate([], {
        relativeTo: this.activatedroute,
        queryParams: { clientName: accountName },
        queryParamsHandling: 'merge'
    });
    this.searchInputClientField.nativeElement.value = accountName;
}

async loadIllustrations(params = this.params, loadMore = false) {
    this.loadingIllustrations = true;
    let newItems;

    if (params?.get("networkMemberId") && params.has("clientId")) {
        this.networkMember = await this.networkMemberService.getNetworkMemberById(params.get("networkMemberId"));
    }

    const nmId = params?.get("networkMemberId") ?? localStorage.getItem('networkMemberId');
    const clientName = params?.get("clientName");
    const subscriberId = params?.get('subscriberId');
    const subscriberEmail = subscriberId ? (await this.userService.getUserById(subscriberId)).preferredEmail : null;

    if (!loadMore) {
        this.illustrations = [];
        this.page = 1;
    }

    if (this.isNetworkMember) {
        newItems = await this.illustrationService.getMyOrganizationIllustrationsPaginated(
            clientName, null, subscriberId, subscriberEmail, null, null, null, this.page, this.pageSize
        );
    }
    
    this.hideShowMoreBtn = newItems.length < this.pageSize;
    this.illustrations = [...this.illustrations, ...newItems.map(illustration => ({
        ...illustration,
        illustrationStage: { ...illustration.illustrationStage, name: illustration.illustrationStage.name !== "Finalized" ? "In-progress" : "Finalized" }
    }))];
    
    document.getElementById("illustrations-view-btn")?.classList.toggle("d-none", this.illustrations.length === 0);
    this.loadingIllustrations = false;
}

async clearSearch() {
    localStorage.removeItem('clientId');
    localStorage.removeItem('clientName');
    this.router.navigate([], {
        relativeTo: this.activatedroute,
        queryParams: { clientName: null, clientId: null },
        queryParamsHandling: 'merge'
    });
}

async addSubscriber(event) {
    this.displaySubs.push(event.user);
}

async fetchClient(term) {
    if (!term) return (this.filteredClients = []);
    this.newClient = false;
    this.searchingClient = true;
    if(this.isCountySelected){
      this.filteredClients = await this.accountsService.getAccountsByState(this.selectedCounty.countyId,term.toLowerCase(), 0, 10);
    }else{
      this.filteredClients = await this.accountsService.getPooledAndProspectAccountsByName(term.toLowerCase(), 0, 10);
    }
    this.searchingClient = false;
}

async fetchCountry(term) {
    if (!term) return (this.filteredCountries = []);
    this.searchingCountry = true;
    this.filteredCountries = this.countryService.countries.filter(x => 
      x.name.replace(/\./g, '').toLowerCase().includes(term.replace(/\./g, '').toLowerCase())
  );
    this.searchingCountry = false;
}

async fetchCounty(term) {
    if (!term) return (this.filteredCounties = []);
    this.searchingCounty = true;
    this.filteredCounties = this.countryService.counties.filter(x => x.name.toLowerCase().includes(term.toLowerCase()));
    console.log(this.filteredCounties);
    this.searchingCounty = false;
}

 // called when selecting a client
 selectClient(client: AccountDto) {
  // hold the selected client
  this.selectedClient = client;
  // empty the list of clients
  this.filteredClients = [];
  if (client) {
    // set the client name
    this.searchInputClientField.nativeElement.value = client.accountName;
    this.illustrationForm.controls.ClientName.setValue(client.accountName);
    this.illustrationForm.controls.AccountRepresentative.setValue(client.divisionalManager.userInfo.email)
    this.illustrationForm.controls.RegionalDirector.setValue(client.accountManager.userInfo.email)
    this.loadClientByName(client.accountName)
    this.newClient = false;
  }
  else {
    this.selectedClient = new AccountDto()
    this.illustrationForm.controls.AccountRepresentative.setValue("")
    this.illustrationForm.controls.RegionalDirector.setValue("")
    this.loadClientByName(this.illustrationForm.controls.ClientName.value)
    this.newClient = true
  }
  this.requestIllustration.clientName = this.illustrationForm.controls.ClientName.value
  this.showDiscussion = true
}

selectCountry(country) {
  this.selectedCountry = country;
  this.filteredCountries = [];
  this.isCountrySelected = true;
  this.searchInputCountryField.nativeElement.value = country.name;
  this.illustrationForm.controls.Country.setValue(country.name);

  if (country.name === "U.S.A.") {
    this.illustrationForm.controls.ClientName.disable();
      this.isUSA = true;
      this.document.getElementsByTagName('body')[0].onclick = (event: MouseEvent) => {
          if (event.target !== this.document.getElementById('searchCountyName')) {
              this.filteredCounties = [];
          }
      };

      this.subscriptions.push(
          fromEvent<KeyboardEvent>(this.searchInputCountyField.nativeElement, 'keyup')
              .pipe(
                  map(event => (event.target as HTMLInputElement).value),
                  debounceTime(100),
                  distinctUntilChanged()
              )
              .subscribe(text => this.fetchCounty(text))
      );

      this.illustrationForm.controls.State.setValidators([Validators.required]);
      
  } else {
      this.isUSA = false;
      this.illustrationForm.controls.ClientName.enable();
  }
}

selectCounty(county) {
    this.selectedCounty = county;
    this.isCountySelected = true
    this.filteredCounties = [];
    this.illustrationForm.controls.ClientName.enable();
    this.searchInputCountyField.nativeElement.value = county.name;
    this.illustrationForm.controls.State.setValue(county.name);
}


removeFile(index) {
    this.uploadedFiles.splice(index, 1);
}

async save() {
  const delay = (ms: number) => new Promise(res => setTimeout(res, ms));

  this.createIllustration(); 
  await delay(1000);

  console.log("Waited 5s");
  this.router.navigateByUrl("/all-illustrations");
}

deleteSubscriber(email) {
    this.displaySubs.splice(this.displaySubs.indexOf(email), 1);
}

sendRequestBySendingComment(event){
  this.save()
}

async createIllustration() {
    this.sendingRequest = true;
    const networkMemberId = (await this.userService.getCurrentUser()).networkMemberId;
    const insuropeSubscribers = [];
    if (this.selectedCountry.name === "U.S.A.") {
        
    }
    const illustrationRequestInputMetadata = new IllustrationRequestInputMetadata();
    if(!this.newClient){
   
      const accountManager = this.selectedClient.accountManager.userInfo;
      const divManager = this.selectedClient.divisionalManager.userInfo;
      if(accountManager.email != ""){
        Object.assign(illustrationRequestInputMetadata.AccountManager, accountManager);
        insuropeSubscribers.push(accountManager.email);
      }
      if(divManager.email != ""){
        Object.assign(illustrationRequestInputMetadata.DivisionalManager, divManager);
        insuropeSubscribers.push(divManager.email);
      }

    }
   
    var status = new Status()
    status.value = TasksStatus.Open
    //using HQ name as sourceId for upserting
    var sourceId = this.requestIllustration.clientName

    try {
        this.informativeService.UpsertInformative(
            networkMemberId, this.requestIllustration.clientName,
            this.selectedCountry.name, this.selectedClient.id,
            this.requestIllustration.processId, this.requestIllustration.id,
            JSON.stringify(illustrationRequestInputMetadata), insuropeSubscribers,status,sourceId,this.selectedCountry.countryId,null
        );
        this.popupNotificationsService.showMessage("The illustration has been created successfully",MessageType.Success)
    } catch (e) {
        this.popupNotificationsService.showMessage("Error when creating a new illustration request",MessageType.Error)
    }
    this.sendingRequest = false;
    this.illustrationForm.reset();
    this.clearSearch();
}
}
